import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Save, ArrowLeft, Loader2, AlertTriangle, CalendarX, Plane } from 'lucide-react';
import { toast } from 'sonner';
import { DocumentUploader } from '@/components/Uploaders';

export default function AgendaFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const editing = !!id;

  const [pejabats, setPejabats] = useState([]);
  const [form, setForm] = useState({
    nama_acara: '', deskripsi: '',
    tanggal_mulai: '', tanggal_selesai: '',
    lokasi: '', kategori: 'Resmi', tingkat_urgensi: 'Normal',
    pejabat_utama: 'Wali Kota', pejabat_utama_id: '',
    delegasi_id: '', delegasi_nama: '', disposisi_catatan: '',
    pendamping_nama: '', catatan: '', pakaian: '', pic_kontak: '',
    dokumen: [],
  });
  const [loading, setLoading] = useState(editing);
  const [submitting, setSubmitting] = useState(false);
  const [conflictData, setConflictData] = useState(null);
  const [checkingConflict, setCheckingConflict] = useState(false);

  useEffect(() => {
    let targetPejabat = null;
    if (form.delegasi_id) {
      targetPejabat = pejabats.find((p) => p.id === form.delegasi_id);
    } else {
      targetPejabat = pejabats.find((p) =>
        p.tingkat_jabatan === form.pejabat_utama ||
        p.jabatan?.toLowerCase().includes(form.pejabat_utama?.toLowerCase())
      );
    }

    if (targetPejabat && form.tanggal_mulai && form.tanggal_selesai) {
      try {
        const startIso = new Date(form.tanggal_mulai).toISOString();
        const endIso = new Date(form.tanggal_selesai).toISOString();
        setCheckingConflict(true);
        api.post('/agenda/check-conflict', {
          pejabat_id: targetPejabat.id,
          tanggal_mulai: startIso,
          tanggal_selesai: endIso,
          exclude_agenda_id: id || null,
        }).then((res) => {
          setConflictData(res.data.has_conflict ? { ...res.data, pejabat_nama: targetPejabat.nama } : null);
        }).catch(() => {
          setConflictData(null);
        }).finally(() => {
          setCheckingConflict(false);
        });
      } catch (e) {
        setConflictData(null);
      }
    } else {
      setConflictData(null);
    }
  }, [form.pejabat_utama, form.delegasi_id, form.tanggal_mulai, form.tanggal_selesai, pejabats, id]);

  useEffect(() => {
    api.get('/pejabat?is_active=true').then((r) => setPejabats(r.data));
    if (editing) {
      api.get(`/agenda/${id}`).then((r) => {
        const a = r.data;
        setForm({
          nama_acara: a.nama_acara || '',
          deskripsi: a.deskripsi || '',
          tanggal_mulai: (a.tanggal_mulai || '').slice(0, 16),
          tanggal_selesai: (a.tanggal_selesai || '').slice(0, 16),
          lokasi: a.lokasi || '',
          kategori: a.kategori || 'Resmi',
          tingkat_urgensi: a.tingkat_urgensi || 'Normal',
          pejabat_utama: a.pejabat_utama || 'Wali Kota',
          pejabat_utama_id: a.pejabat_utama_id || '',
          delegasi_id: a.delegasi_id || '',
          delegasi_nama: a.delegasi_nama || '',
          disposisi_catatan: a.disposisi_catatan || '',
          pendamping_nama: (a.pendamping_nama || []).join(', '),
          catatan: a.catatan || '',
          pakaian: a.pakaian || '',
          pic_kontak: a.pic_kontak || '',
          dokumen: a.dokumen || [],
        });
      }).finally(() => setLoading(false));
    }
  }, [editing, id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nama_acara || !form.tanggal_mulai || !form.tanggal_selesai || !form.lokasi) {
      toast.error('Mohon lengkapi field wajib: Nama Acara, Tanggal, Lokasi');
      return;
    }
    setSubmitting(true);
    try {
      const delegasiPej = pejabats.find((p) => p.id === form.delegasi_id);
      const payload = {
        ...form,
        tanggal_mulai: new Date(form.tanggal_mulai).toISOString(),
        tanggal_selesai: new Date(form.tanggal_selesai).toISOString(),
        pendamping_nama: form.pendamping_nama.split(',').map((s) => s.trim()).filter(Boolean),
        pendamping_ids: [],
        delegasi_nama: delegasiPej ? delegasiPej.nama : (form.delegasi_nama || ''),
        delegasi_id: form.delegasi_id || null,
        pejabat_utama_id: form.pejabat_utama_id || null,
        dokumen: form.dokumen || [],
      };
      if (editing) {
        await api.patch(`/agenda/${id}`, payload);
        toast.success('Agenda berhasil diperbarui');
      } else {
        await api.post('/agenda', payload);
        toast.success('Agenda berhasil dibuat dan menunggu persetujuan Kabag');
      }
      navigate('/agenda');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menyimpan agenda');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="py-16 flex justify-center"><div className="animate-spin w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6 max-w-5xl">
      <PageHeader
        title={editing ? 'Edit Agenda' : 'Tambah Agenda Baru'}
        subtitle="Isi detail agenda pimpinan. Setelah disimpan, agenda akan menunggu persetujuan Kabag Prokopim."
        breadcrumb={[{ label: 'Beranda', to: '/dashboard' }, { label: 'Agenda', to: '/agenda' }, { label: editing ? 'Edit' : 'Tambah' }]}
        action={<Button variant="outline" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4 mr-2" />Kembali</Button>}
      />

      <form onSubmit={submit} className="space-y-5">
        <Card className="border-slate-200 shadow-premium">
          <CardHeader><CardTitle className="text-base font-bold">Informasi Dasar</CardTitle></CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2 space-y-1.5">
              <Label>Nama Acara <span className="text-red-600">*</span></Label>
              <Input data-testid="agenda-nama" value={form.nama_acara} onChange={(e) => setForm({ ...form, nama_acara: e.target.value })} placeholder="Contoh: Rapat Koordinasi OPD" />
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label>Deskripsi</Label>
              <Textarea rows={3} value={form.deskripsi} onChange={(e) => setForm({ ...form, deskripsi: e.target.value })} placeholder="Penjelasan singkat tentang agenda…" />
            </div>
            <div className="space-y-1.5">
              <Label>Kategori</Label>
              <Select value={form.kategori} onValueChange={(v) => setForm({ ...form, kategori: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Resmi">Resmi</SelectItem>
                  <SelectItem value="Kunjungan">Kunjungan</SelectItem>
                  <SelectItem value="Rapat">Rapat</SelectItem>
                  <SelectItem value="Seremonial">Seremonial</SelectItem>
                  <SelectItem value="Pribadi">Pribadi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Tingkat Urgensi</Label>
              <Select value={form.tingkat_urgensi} onValueChange={(v) => setForm({ ...form, tingkat_urgensi: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Normal">Normal</SelectItem>
                  <SelectItem value="Penting">Penting</SelectItem>
                  <SelectItem value="Mendesak">Mendesak</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-premium">
          <CardHeader><CardTitle className="text-base font-bold">Waktu & Lokasi</CardTitle></CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Waktu Mulai <span className="text-red-600">*</span></Label>
              <Input data-testid="agenda-mulai" type="datetime-local" value={form.tanggal_mulai} onChange={(e) => setForm({ ...form, tanggal_mulai: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Waktu Selesai <span className="text-red-600">*</span></Label>
              <Input data-testid="agenda-selesai" type="datetime-local" value={form.tanggal_selesai} onChange={(e) => setForm({ ...form, tanggal_selesai: e.target.value })} />
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label>Lokasi <span className="text-red-600">*</span></Label>
              <Input data-testid="agenda-lokasi" value={form.lokasi} onChange={(e) => setForm({ ...form, lokasi: e.target.value })} placeholder="Contoh: Ruang Pola Setda Pemkot Makassar" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-premium">
          <CardHeader><CardTitle className="text-base font-bold">Delegasi & Pendamping</CardTitle></CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Pejabat Utama</Label>
              <Select value={form.pejabat_utama} onValueChange={(v) => setForm({ ...form, pejabat_utama: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Wali Kota">Wali Kota</SelectItem>
                  <SelectItem value="Wakil Wali Kota">Wakil Wali Kota</SelectItem>
                  <SelectItem value="Sekretaris Daerah">Sekretaris Daerah</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Delegasi (opsional)</Label>
              <Select value={form.delegasi_id || 'none'} onValueChange={(v) => setForm({ ...form, delegasi_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="agenda-delegasi"><SelectValue placeholder="Pilih delegasi" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Tanpa Delegasi</SelectItem>
                  {pejabats.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.nama} – {p.jabatan}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label>Disposisi / Catatan Pimpinan untuk Delegasi</Label>
              <Textarea rows={2} value={form.disposisi_catatan} onChange={(e) => setForm({ ...form, disposisi_catatan: e.target.value })} placeholder="Catatan / arahan pimpinan kepada delegasi" />
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label>Pendamping (pisahkan dengan koma)</Label>
              <Input value={form.pendamping_nama} onChange={(e) => setForm({ ...form, pendamping_nama: e.target.value })} placeholder="Protokol Setda, Ajudan Pimpinan, dll." />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-premium">
          <CardHeader><CardTitle className="text-base font-bold">Detail Tambahan</CardTitle></CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Pakaian</Label>
              <Input value={form.pakaian} onChange={(e) => setForm({ ...form, pakaian: e.target.value })} placeholder="PSL / PDH / Batik / Adat" />
            </div>
            <div className="space-y-1.5">
              <Label>PIC / Kontak</Label>
              <Input value={form.pic_kontak} onChange={(e) => setForm({ ...form, pic_kontak: e.target.value })} placeholder="Nama PIC atau OPD penyelenggara" />
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label>Catatan Internal</Label>
              <Textarea rows={3} value={form.catatan} onChange={(e) => setForm({ ...form, catatan: e.target.value })} />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-premium">
          <CardHeader><CardTitle className="text-base font-bold">Dokumen Pendukung</CardTitle></CardHeader>
          <CardContent>
            <DocumentUploader
              items={form.dokumen}
              onChange={(items) => setForm({ ...form, dokumen: items })}
              endpoint="/upload/dokumen-agenda"
              kindOptions={[
                { value: 'Undangan', label: 'Undangan' },
                { value: 'Rundown', label: 'Rundown Acara' },
                { value: 'Nota Dinas', label: 'Nota Dinas' },
                { value: 'Lampiran', label: 'Lampiran Lain' },
              ]}
            />
          </CardContent>
        </Card>

        {conflictData && conflictData.has_conflict && (
          <div className="p-4 rounded-xl border-2 border-amber-300 bg-amber-50/90 shadow-sm space-y-3">
            <div className="flex items-center gap-2 text-amber-900 font-bold text-sm">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 animate-pulse" />
              <span>PERINGATAN BENTROK JADWAL: {conflictData.pejabat_nama} telah memiliki kegiatan lain pada waktu ini!</span>
            </div>

            {conflictData.agenda_conflicts?.length > 0 && (
              <div className="space-y-1.5 pl-7">
                <div className="text-xs font-semibold uppercase tracking-wider text-amber-800 flex items-center gap-1.5">
                  <CalendarX className="w-3.5 h-3.5" /> Agenda yang Bertabrakan ({conflictData.agenda_conflicts.length}):
                </div>
                <div className="grid gap-1.5 sm:grid-cols-2">
                  {conflictData.agenda_conflicts.map((c) => (
                    <div key={c.id} className="text-xs bg-white/90 p-2.5 rounded border border-amber-200 shadow-sm">
                      <div className="font-semibold text-slate-800">{c.nama_acara}</div>
                      <div className="text-slate-600 mt-0.5">
                        {new Date(c.tanggal_mulai).toLocaleString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} s/d {new Date(c.tanggal_selesai).toLocaleString('id-ID', { hour: '2-digit', minute: '2-digit' })} WITA
                      </div>
                      <div className="text-slate-500 text-[11px] truncate">Lokasi: {c.lokasi}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {conflictData.sppd_conflicts?.length > 0 && (
              <div className="space-y-1.5 pl-7">
                <div className="text-xs font-semibold uppercase tracking-wider text-amber-800 flex items-center gap-1.5">
                  <Plane className="w-3.5 h-3.5" /> Sedang Perjalanan Dinas / SPPD ({conflictData.sppd_conflicts.length}):
                </div>
                <div className="grid gap-1.5 sm:grid-cols-2">
                  {conflictData.sppd_conflicts.map((s) => (
                    <div key={s.id} className="text-xs bg-white/90 p-2.5 rounded border border-amber-200 shadow-sm">
                      <div className="font-semibold text-slate-800">{s.nomor_sppd}</div>
                      <div className="text-slate-600 mt-0.5 font-medium">{s.keperluan}</div>
                      <div className="text-slate-500 text-[11px]">Tujuan: {s.tujuan_kota || s.negara_tujuan || '-'} ({s.tanggal_berangkat?.slice(0, 10)} s/d {s.tanggal_kembali?.slice(0, 10)})</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="text-[11px] text-amber-800 italic pl-7">
              * Anda tetap dapat melanjutkan pengajuan jika penugasan ini mendesak atau bersifat tentatif.
            </div>
          </div>
        )}

        <div className="sticky bottom-4 z-10">
          <Card className="border-slate-200 shadow-elevated">
            <CardContent className="p-3 flex flex-wrap items-center justify-between gap-2">
              <div className="text-xs text-slate-500 hidden sm:block">Field bertanda <span className="text-red-600 font-semibold">*</span> wajib diisi.</div>
              <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                <Button type="button" variant="outline" data-testid="form-cancel-button" onClick={() => navigate(-1)}>Batal</Button>
                <Button type="submit" disabled={submitting} data-testid="form-submit-button" className="bg-[#DC2626] hover:bg-[#B91C1C]">
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Menyimpan…</> : <><Save className="w-4 h-4 mr-2" />{editing ? 'Simpan Perubahan' : 'Simpan & Ajukan'}</>}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </form>
    </div>
  );
}
